import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { notificationService } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { FiBell, FiMail, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

const PageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
`;

const PreferencesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const PreferenceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:first-child {
    width: 60%;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  vertical-align: middle;
`;

const NotificationLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const NotificationTitle = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const NotificationDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// The label is the tap target: it matches the 44x22 slider on desktop and
// grows to a >=44px tappable area on mobile (the visual slider keeps its size
// and stays centered inside).
const Toggle = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 22px;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 44px;
  }
`;

const Slider = styled.span`
  display: block;
  flex: 0 0 auto;
  width: 44px;
  height: 22px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.border};
  transition: .3s;
  border-radius: 34px;
  position: relative;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
  }

  input:checked + & {
    background-color: ${({ theme }) => theme.colors.success};
  }

  input:checked + &:before {
    transform: translateX(22px);
  }
`;

const AlertMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
  
  ${({ type, theme }) => {
    if (type === 'success') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (type === 'error') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[6]};

  @media (max-width: 576px) {
    & > button {
      width: 100%;
      min-height: 48px;
    }
  }
`;

// Preference shape returned by the API: { email: {...}, inApp: {...} } with
// five booleans per channel. Merge over defaults so missing keys never crash.
const DEFAULT_PREFERENCES = {
  email: {
    timeEntryApproval: true,
    timeEntryRejection: true,
    newAssignment: true,
    systemUpdates: true,
    reminders: true
  },
  inApp: {
    timeEntryApproval: true,
    timeEntryRejection: true,
    newAssignment: true,
    systemUpdates: true,
    reminders: true
  }
};

const mergePreferences = (data) => ({
  email: { ...DEFAULT_PREFERENCES.email, ...((data && data.email) || {}) },
  inApp: { ...DEFAULT_PREFERENCES.inApp, ...((data && data.inApp) || {}) }
});

const NOTIFICATION_TYPES = [
  'timeEntryApproval',
  'timeEntryRejection',
  'newAssignment',
  'systemUpdates',
  'reminders'
];

const NotificationPreferences = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setAlert(null);
      const data = await notificationService.getNotificationPreferences();
      setPreferences(mergePreferences(data));
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      setPreferences(null);
      setAlert({
        type: 'error',
        message: t('employee.notifications.loadError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = (channel, type) => {
    setPreferences(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [type]: !prev[channel][type]
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const saved = await notificationService.updateNotificationPreferences(preferences);
      setPreferences(mergePreferences(saved));
      setAlert({
        type: 'success',
        message: t('employee.notifications.saveSuccess')
      });

      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlert(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      setAlert({
        type: 'error',
        message: t('employee.notifications.saveError')
      });
    } finally {
      setSaving(false);
    }
  };

  const renderPreferenceRows = (channel, channelNoun) =>
    NOTIFICATION_TYPES.map((key) => (
      <TableRow key={key}>
        <TableCell>
          <NotificationLabel>
            <NotificationTitle>{t(`employee.notifications.types.${key}.title`)}</NotificationTitle>
            <NotificationDescription>{t(`employee.notifications.types.${key}.description`, { channel: channelNoun })}</NotificationDescription>
          </NotificationLabel>
        </TableCell>
        <TableCell>
          <Toggle>
            <input
              type="checkbox"
              checked={Boolean(preferences[channel][key])}
              onChange={() => handleToggleChange(channel, key)}
            />
            <Slider />
          </Toggle>
        </TableCell>
      </TableRow>
    ));

  if (loading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>{t('employee.notifications.title')}</PageTitle>
        </PageHeader>
        <Card padding="md">
          <div>{t('employee.notifications.loading')}</div>
        </Card>
      </PageContainer>
    );
  }

  if (!preferences) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>{t('employee.notifications.title')}</PageTitle>
        </PageHeader>
        <Card padding="md">
          {alert && (
            <AlertMessage type={alert.type}>
              <FiAlertCircle />
              {alert.message}
            </AlertMessage>
          )}
          <div>{t('employee.notifications.loadFailedShort')}</div>
          <FormActions>
            <Button onClick={fetchPreferences}>{t('common.retry')}</Button>
          </FormActions>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>{t('employee.notifications.title')}</PageTitle>
      </PageHeader>
      
      <Card padding="md">
        {alert && (
          <AlertMessage type={alert.type}>
            {alert.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
            {alert.message}
          </AlertMessage>
        )}
        
        <PreferencesContainer>
          <div>
            <SectionTitle>
              <FiBell />
              {t('employee.notifications.inAppTitle')}
            </SectionTitle>
            <PreferenceTable>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('employee.notifications.columnType')}</TableHeadCell>
                  <TableHeadCell>{t('employee.notifications.columnEnabled')}</TableHeadCell>
                </tr>
              </TableHead>
              <tbody>
                {renderPreferenceRows('inApp', t('employee.notifications.channel.notification'))}
              </tbody>
            </PreferenceTable>
          </div>

          <div>
            <SectionTitle>
              <FiMail />
              {t('employee.notifications.emailTitle')}
            </SectionTitle>
            <PreferenceTable>
              <TableHead>
                <tr>
                  <TableHeadCell>{t('employee.notifications.columnType')}</TableHeadCell>
                  <TableHeadCell>{t('employee.notifications.columnEnabled')}</TableHeadCell>
                </tr>
              </TableHead>
              <tbody>
                {renderPreferenceRows('email', t('employee.notifications.channel.email'))}
              </tbody>
            </PreferenceTable>
          </div>
        </PreferencesContainer>
        
        <FormActions>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('employee.notifications.save')}
          </Button>
        </FormActions>
      </Card>
    </PageContainer>
  );
};

export default NotificationPreferences;